import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { XMarkIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import ResumePreview from './ResumePreview';
import TemplateSelector from './TemplateSelector';

const MobilePreviewDrawer = ({
  isOpen,
  onClose,
  html,
  loading = false,
  hasWatermark = false,
  selectedTemplate,
  onSelectTemplate,
  isPro = false
}) => {
  const drawerRef = useRef(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 bg-black transition-opacity duration-300 z-40
          ${isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`
          fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-y-0' : 'translate-y-full'}
        `}
        style={{ maxHeight: '90vh' }}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Resume Preview</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Template selector */}
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-xs text-gray-500 mb-2">Choose a template:</p>
          <TemplateSelector
            selectedTemplate={selectedTemplate}
            onSelectTemplate={onSelectTemplate}
            isPro={isPro}
          />
        </div>

        {/* Preview */}
        <div className="h-[60vh] overflow-auto">
          <ResumePreview
            html={html}
            loading={loading}
            hasWatermark={hasWatermark}
          />
        </div>
      </div>

      {/* Floating preview button (when drawer is closed) */}
      {!isOpen && html && (
        <button
          onClick={() => onClose()}
          className="fixed bottom-20 right-4 z-30 bg-primary-600 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 hover:bg-primary-700 transition-colors"
        >
          <ChevronUpIcon className="w-5 h-5" />
          <span className="text-sm font-medium">Preview</span>
        </button>
      )}
    </>
  );
};

MobilePreviewDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  html: PropTypes.string,
  loading: PropTypes.bool,
  hasWatermark: PropTypes.bool,
  selectedTemplate: PropTypes.string.isRequired,
  onSelectTemplate: PropTypes.func.isRequired,
  isPro: PropTypes.bool
};

export default MobilePreviewDrawer;
